<template>
  <div id="login" class="min-h-screen relative w-full flex flex-col justify-center">
    <div class="p-5">
      <!-- logging loader -->
      <loader 
        v-if="loading"
        :label="$t('frontend_logging_in')"
      />

      <div
        v-else
        class="
          flex
          flex-col
          md:flex-row
          justify-between
          lg:container
          mx-auto
          shadow-lg
          rounded-lg
          bg-white
        "
      >
        <!-- Login Form -->
        <div class="flex flex-col justify-center md:w-5/12 lg:w-7/12 my-5">
          <div id="logo" class="ml-4 lg:ml-12 flex flex-col w-6/12">
            <span class="text-grey-700 sm:front-semibold heading-lg">
              <gd-logo class="h-8 w-8 inline-block mr-1"></gd-logo>Groove
            </span>
          </div>
          <div class="flex flex-col items-center mb-10 mt-20 md:mb-0 md:ml-28">
            <div class="m-2 sm:m-0 w-11/12 lg:w-6/12 pb-2">
              <span class="text-grey-700 heading">
                {{ $t('frontend_sites_Login_page_login') }}
              </span>
            </div>
            <div class="m-2 w-11/12 lg:w-6/12 pb-2">
              <div class="mb-1">
                <label for="email" class="text-grey-700 defaultText">{{
                  $t('frontend_sites_Login_page_email')
                }}</label>
              </div>
              <input
                @keyup.enter="login"
                id="email"
                v-model.trim="$v.username.$model"
                type="text"
                placeholder="email"
                class="w-full h-12 border rounded p-4"
                :class="{
                  'border-pink-dark': $v.username.$error,
                  'border-grey-300': !$v.username.$error,
                }"
              />
              <div v-if="$v.username.$error" class="error text-pink-dark mt-1 text-sm">
                <div
                  v-if="!$v.username.required"
                >
                  {{ $t('frontend_sites_Login_page_email_required') }}
                </div>
                <div
                  v-if="!$v.username.minLength"
                >
                  {{ $t('frontend_sites_Login_page_email_must_have') }}
                  {{ $v.username.$params.minLength?.min || '6'}}
                  {{ $t('frontend_sites_Login_page_letters') }}.
                </div>
                <div
                  v-if="!$v.username.email"
                >
                  {{ $t('frontend_sites_Login_page_write_valid_email') }}
                </div>
              </div>
            </div>

            <div class="m-2 sm:m-0 w-11/12 lg:w-6/12">
              <div class="mb-1">
                <label for="password" class="text-grey-700 defaultText">{{
                  $t('frontend_sites_Login_page_password')
                }}</label>
              </div>
              <input
                @keyup.enter="login"
                id="password"
                v-model="$v.password.$model"
                type="password"
                placeholder="password"
                class="w-full h-12 border rounded p-4"
                :class="{
                  'border-pink-dark': $v.password.$error,
                  'border-grey-300': !$v.password.$error,
                }"
              />
              <div v-if="$v.password.$error" class="error text-pink-dark mt-1 text-sm">
                <div
                  v-if="!$v.password.required"
                >
                  {{ $t('frontend_sites_Login_page_password_required') }}
                </div>
                <div
                  v-if="!$v.password.minLength"
                >
                  {{ $t('frontend_sites_Login_page_password_must_have') }}
                  {{ $v.password.$params.minLength?.min || '6' }}
                  {{ $t('frontend_sites_Login_page_letters') }}.
                </div>
                <div
                 v-if="!$v.password.maxLength"
                >
                 {{ $t('frontend_sites_Login_page_password_must_have_max_limit') }}
                 {{ $v.password.$params.maxLength.max }}
                 {{ $t('frontend_sites_Login_page_letters') }}.
               </div>
              </div>
            </div>
            <div class="m-2 w-11/12 lg:w-6/12 pb-2">
              <span
                class="
                  text-pink
                  float-right
                  cursor-pointer
                  heading-sm
                  sm:font-normal
                "
              >
                <router-link id="forgot_password_link" to="/forgotPassword">{{
                  $t('frontend_sites_Login_page_forgot_password')
                }}</router-link>
              </span>
            </div>
            <div id="login_button" class="m-2 w-11/12 lg:w-6/12">
              <gd-button
                :primary="true"
                class="w-full h-12 sm:font-normal"
                @click="login"
                >{{ $t('frontend_login') }}</gd-button
              >
            </div>
            <div v-text="error"></div>
            <div class="m-2 w-11/12 lg:w-6/12">
              <gd-button class="w-full h-12 sm:font-normal" @click="signup">{{
                $t('frontend_gduikit_sign_up')
              }}</gd-button>
            </div>
          </div>
        </div>

        <!-- Ad Image -->
        <div class="flex flex-col md:w-7/12 lg:w-5/12 lg:max-w-lg">
          <a :href="triggerURL" target="_blank">
            <img :src="adImageURL" alt class="w-full rounded-r-lg" />
          </a>
        </div>
      </div>

      <!-- Signup Modal -->
      <gd-modal 
        ref="modal"
        name="signup"
        width="500px"
      >
        <div class="flex flex-col items-center justify-between" style="min-height: 500px;">
          <div style=" width: 100%; max-width:500px;" class="sing-up">
            <link href="https://gcm.groovesell.com/embed/css/app.css?n=1" rel="stylesheet">
            <groovesell-embed subdomain="gcm" :checkout="CheckoutWidgetId" />
          </div>

          <div class="flex w-full justify-center pt-3 pb-6">
            <gd-button
              @click="$refs.modal.close()"
            >
              {{ $t('frontend_cancel')}}
            </gd-button>
          </div>
        </div>
      </gd-modal>
    </div>
  </div>
</template>
<script>
import api from '@groovepages/gd-lib/api';
import auth from '@groovepages/gd-lib/auth';
import { email, minLength, maxLength, required } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import Loader from './Loader';
import GdButton from './Button';
import GdLogo from '../assets/logo-extra-small.svg';
import GdModal from './Modal';

export default {
  components: {
    Loader,
    GdButton,
    GdLogo,
    GdModal,
  },
  mixins: [validationMixin],
  props: {
    callback: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      triggerURL: 'https://groovedigital.com', // Default for Fallback
      adImageURL: '../assets/login-image.png', // '../assets/login-image.png', //Default for Fallback
      username: '',
      password: '',
      error: '',
      loading: false,
    };
  },
  computed: {
    CheckoutWidgetId() {
      let widgetEnv = 'SIGNUP_CHECKOUT_WIDGET_ID';
      return window.gdEnv[widgetEnv];
    },
    page_lang() {
      return this.$root.$i18n.locale;
    },
  },
  mounted() {
    this.getAdImageURL();
    // Load GrooveSell Embed Script for Signup Modal on mounted
    this.loadScript();
    window.addEventListener('message', (message) => {
      if (typeof message.data !== 'string') return;
      try {
        const command = JSON.parse(message.data);
        if (command.type === 'groovesell-successful-transaction') {
          window.location.replace(command.redirect);
        }
      } catch (e) {}
    });
  },
  validations: {
    username: {
      required,
      email,
      minLength: minLength(6),
    },
    password: {
      required,
      minLength: minLength(6),
      maxLength: maxLength(30),
    },
  },
  methods: {
    getAdImageURL() {
      api
        .get(`${window.gdEnv.VUE_APP_API_URL}/groovedigital/ad/random`)
        .then((data) => {
          this.adImageURL = data.image_url;
          this.triggerURL = data.trigger_url;
        });
    },

    // Load GrooveSell Embed Script for Signup Modal
    loadScript() {
      const script = document.createElement('script');
      script.src = "https://gcm.groovesell.com/embed/js/app.js?n=1";
      script.async = true;
      document.body.appendChild(script);
    },

    signup() {
      this.$refs.modal.open();
    },
    login() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.submitStatus = 'ERROR';
      } else {
        this.error = '';
        this.loading = true;
        auth
          .login(this.username, this.password)
          .then((user) => {
            this.loading = false;
            this.callback(user);
          })
          .catch((err) => {
            this.loading = false;
            this.error = err.data.message;
          });
      }
    },
    waitingList() {
      this.$refs.modal.open();
    },
  },
};
</script>

<style lang="scss">
  #login {
    height: calc(100% + 3.25rem);
  }
</style>